
// ─────────────────────────────────────────────────────────────────────────
// OBSERVED SURFACE SUPPORT — pure geometry, no THREE/DOM dependency (only
// 2D {x,y} points relative to a cross-section's own centre, in the plane
// perpendicular to the local tangent).
//
// Root cause this exists to fix (Find rør real-data test PIPE-20260901-002):
// the tracker's old `computeAngularCoverage` (cylinderFit.js) counts a 10°
// angular bin as "covered" the moment ANY point within the radial inlier
// band falls in it - regardless of how many points, how radially consistent
// they are, or whether neighbouring bins are also covered. In a real
// utility trench, soil clumps, other cables and debris frequently sit at
// close to the same radial distance from the (already fixed) axis as the
// pipe itself, at essentially every angle, in large photogrammetric point
// counts. With enough candidate density, EVERY one of 36 bins gets at least
// one contaminating hit purely by chance, so raw occupancy saturates near
// 360° even when the true pipe surface is only genuinely visible over a
// fraction of the circumference (this is the mechanism behind the reported
// ~339° "coverage" on a physically partially-buried pipe).
//
// The fix distinguishes:
//   rawCoverageDeg       - the old bin-occupancy measure (kept as a diagnostic)
//   supportedCoverageDeg - degrees of the estimated pipe surface that are
//                          backed by a CONTIGUOUS, sufficiently dense,
//                          radially-consistent run of sectors, preferring the
//                          run that overlaps a supplied "anchor" direction
//                          (the previous section's own observed arc, or the
//                          user's own seed points at initialisation - spec
//                          "Brug brugerens surface seeds som soft anchor").
//
// A sector is a SUPPORTED SECTOR only if it has enough points (both an
// absolute floor and a floor relative to the section's own average density,
// so a mostly-empty section doesn't need many points per sector to still
// count, but a single stray hit in an otherwise busy section does not) AND
// those points' own radial spread is consistent with a single coherent
// surface (a tight median, not scattered across the whole inlier band).
//
// The chosen arc may grow, shrink or shift angularly between calls (nothing
// here locks it to the anchor) - the anchor only breaks ties between
// multiple similarly-supported candidate arcs and is itself updated only by
// a slow blend (in pipeTracking.js), so it cannot snap straight from "half
// the pipe" to "all the way round" just because clutter briefly surrounds it.
// ─────────────────────────────────────────────────────────────────────────

function median(values){
	if(values.length === 0) return 0;
	const s = [...values].sort((a, b) => a - b);
	const n = s.length;
	return n % 2 ? s[(n - 1) / 2] : (s[n / 2 - 1] + s[n / 2]) / 2;
}

// Index of some sector inside the widest circular run of `false` values -
// used purely to choose a safe place to "cut" the circle for a linear scan.
function widestFalseGapStart(supported){
	const n = supported.length;
	let bestStart = 0, bestLen = -1;
	let i = 0;
	while(i < n){
		if(supported[i]){ i++; continue; }
		let len = 0, j = i;
		while(j < i + n && !supported[j % n]){ len++; j++; }
		if(len > bestLen){ bestLen = len; bestStart = i; }
		i = j;
	}
	return bestStart;
}

// Contiguous (circular) runs of `true` sectors, allowed to bridge gaps of up
// to `gapSectors` consecutive `false` sectors (a single missed 10° bin
// between two well-supported groups is far more likely sensor shadow than a
// second object). Returns [{startSector, trueMembers:[...], spanSectors}].
// Bridged gap sectors are NOT included in `trueMembers` (they carry no
// evidence) but do count towards `spanSectors` (the arc they sit inside).
export function findSupportedRuns(supported, gapSectors){
	const n = supported.length;
	if(n === 0 || !supported.some(Boolean)) return [];
	if(supported.every(Boolean)) return [{startSector: 0, trueMembers: supported.map((_, i) => i), spanSectors: n}];

	// Rotate the scan to start inside the WIDEST (circular) false gap, not
	// just any false sector: cutting inside the widest gap keeps every
	// narrower, individually-bridgeable gap safely INSIDE the linear scan
	// range, so a legitimate bridge never gets mis-split by sitting exactly
	// on the array's own seam.
	const offset = widestFalseGapStart(supported);
	const rotated = supported.slice(offset).concat(supported.slice(0, offset));

	const runs = [];
	let i = 0;
	while(i < n){
		if(!rotated[i]){ i++; continue; }
		let j = i;
		let lastTrue = i;
		const trueMembers = [];
		while(j < n){
			if(rotated[j]){ trueMembers.push(j); lastTrue = j; j++; continue; }
			let gapLen = 0, k = j;
			while(k < n && !rotated[k]){ gapLen++; k++; }
			if(gapLen <= gapSectors && k < n && rotated[k]){ j = k; }
			else break;
		}
		runs.push({
			startSector: (trueMembers[0] + offset) % n,
			trueMembers: trueMembers.map((s) => (s + offset) % n),
			spanSectors: lastTrue - i + 1,
		});
		i = lastTrue + 1;
	}
	// Edge case the rotation can't see directly: a single run whose only gaps
	// (including the rotation seam itself) are all individually bridgeable
	// sums to an effectively unbroken ring (e.g. one small shadow gap in an
	// otherwise fully observed pipe). Detect it by total missing sectors.
	if(runs.length === 1 && n - runs[0].trueMembers.length <= gapSectors){
		runs[0].spanSectors = n;
	}
	return runs;
}

// Circular distance (in sectors) from `sector` to the nearest sector inside
// [startSector, startSector+spanSectors) mod n; 0 if already inside.
function circularDistanceToRun(sector, run, n){
	for(let k = 0; k < run.spanSectors; k++){
		if((run.startSector + k) % n === sector) return 0;
	}
	const toStart = ((run.startSector - sector) % n + n) % n;
	const fromEnd = ((sector - (run.startSector + run.spanSectors - 1)) % n + n) % n;
	return Math.min(toStart, fromEnd);
}

// points2D: [{x,y}] already relative to the section's own fitted centre, in
// the (u,v) plane ⟂ the local tangent (so radius = hypot(x,y)). `radius`/
// `band` are the section's fixed radius and its (σ_surface-derived) radial
// tolerance. `anchorAngleRad` (nullable): the preferred arc centre, e.g. the
// previous section's own observed-arc direction, or the seed-derived
// direction at initialisation.
export function computeSurfaceSupport(points2D, radius, band, opts, anchorAngleRad = null){
	const n = points2D.length;
	const xs = new Float64Array(n), ys = new Float64Array(n);
	for(let i = 0; i < n; i++){ xs[i] = points2D[i].x; ys[i] = points2D[i].y; }
	return computeSurfaceSupportXY(xs, ys, radius, band, opts, anchorAngleRad);
}

// Samme beregning med x/y som parallelle talarrays (varm sti i Find rør: ingen {x,y}-objekter pr. punkt). computeSurfaceSupport
// ovenfor er en tynd indpakning, så der kun findes én implementering.
export function computeSurfaceSupportXY(xs, ys, radius, band, opts, anchorAngleRad = null){
	const sectorCount = opts.surfaceSectorCount || 36;
	const sectorRad = (Math.PI * 2) / sectorCount;

	const buckets = Array.from({length: sectorCount}, () => []);
	for(let i = 0; i < xs.length; i++){
		const x = xs[i], y = ys[i];
		let a = Math.atan2(y, x);
		if(a < 0) a += Math.PI * 2;
		const s = Math.min(sectorCount - 1, Math.floor(a / sectorRad));
		buckets[s].push(Math.hypot(x, y));
	}

	const occupied = buckets.map((b) => b.length > 0);
	const rawCoverageDeg = (occupied.filter(Boolean).length / sectorCount) * 360;

	// Density-relative floor uses the MEDIAN sector count, not the mean: a
	// handful of sectors with a wildly denser reconstruction (photogrammetric
	// patches are often very unevenly sampled) would otherwise drag the mean
	// up and make every genuinely-observed but normal-density sector of the
	// SAME real surface look "too sparse" - rejecting valid surface, not
	// contamination, and collapsing supported coverage on an evenly-shaped
	// pipe that was simply photographed unevenly. The median is robust to
	// that skew: it tracks the TYPICAL sector, not the few extreme ones.
	const medianPerSector = median(buckets.map((b) => b.length));
	const minAbs = opts.surfaceMinSectorPointsAbs ?? 3;
	const minFrac = opts.surfaceMinSectorDensityFrac ?? 0.15;
	const minSectorPoints = Math.max(minAbs, minFrac * medianPerSector);
	// a real surface's own local radial spread should sit inside a somewhat
	// TIGHTER window than the full noise-tolerant inlier band - scattered
	// contamination across the whole band is exactly what this should reject
	const radialTolerance = band * (opts.surfaceRadialConsistencyFactor ?? 0.85);

	// A sector can be "supported" two ways:
	//  1. ENOUGH points (>= minSectorPoints) AND radially consistent - the
	//     normal case.
	//  2. TOO FEW points to judge by count alone, but the few that ARE there
	//     are TIGHTLY clustered near R (spread well inside the tolerance).
	//     This matters for very unevenly-sampled real photogrammetric
	//     surfaces (one patch reconstructed far denser than the rest of the
	//     SAME real pipe): a genuinely observed sector with only 2-3 points
	//     that agree tightly is real surface, not noise - whereas
	//     contamination sitting loosely somewhere in the band, even with a
	//     similar point count, will NOT cluster this tightly. This is what
	//     keeps density-skew from either (a) hiding real surface behind an
	//     absolute/relative count floor, or (b) letting a few scattered
	//     contamination hits count as "supported" just because they exist.
	const tightSpreadTolerance = radialTolerance * 0.3;
	const supported = buckets.map((b) => {
		if(b.length < 2) return false;
		const med = median(b);
		if(Math.abs(med - radius) > radialTolerance) return false;
		if(b.length >= minSectorPoints) return true;
		const spread = median(b.map((r) => Math.abs(r - med)));
		return spread <= tightSpreadTolerance;
	});

	const gapSectors = opts.surfaceGapSectors ?? 1;
	const minRunSectors = opts.surfaceMinRunSectors ?? 2;
	const runs = findSupportedRuns(supported, gapSectors).filter((r) => r.trueMembers.length >= minRunSectors);

	if(runs.length === 0){
		return {rawCoverageDeg, supportedCoverageDeg: 0, supportedSectorMask: new Array(sectorCount).fill(false), arcCenterAngle: null, arcSpanDeg: 0, sectorCount};
	}

	let best = runs[0];
	if(anchorAngleRad != null){
		const anchorSector = Math.min(sectorCount - 1, Math.floor((((anchorAngleRad % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2)) / sectorRad));
		let bestKey = null;
		for(const r of runs){
			const dist = circularDistanceToRun(anchorSector, r, sectorCount);
			// prefer runs that actually contain/are close to the anchor, then
			// the largest supported run among equally-close candidates
			const key = [dist, -r.trueMembers.length];
			if(bestKey === null || key[0] < bestKey[0] || (key[0] === bestKey[0] && key[1] < bestKey[1])){
				bestKey = key; best = r;
			}
		}
	}else{
		for(const r of runs) if(r.trueMembers.length > best.trueMembers.length) best = r;
	}

	const supportedSectorMask = new Array(sectorCount).fill(false);
	for(const s of best.trueMembers) supportedSectorMask[s] = true;

	const arcCenterAngle = ((best.startSector + best.spanSectors / 2) % sectorCount) * sectorRad;

	// COMPETING-CONTINUATION DETECTION (spec metode 10 - detection only, no
	// branching): the largest OTHER radially-consistent run. A second run of
	// comparable size, well separated from the chosen arc, means there are two
	// plausible pipe surfaces in this cross-section (a parallel utility, a
	// T-junction, a crossing). Reported up the stack so the tracker can flag
	// the ambiguity - it is NOT resolved here.
	let second = null;
	for(const r of runs){
		if(r === best) continue;
		if(!second || r.trueMembers.length > second.trueMembers.length) second = r;
	}
	let secondArcRel = 0, secondArcSeparationDeg = 0;
	if(second){
		secondArcRel = second.trueMembers.length / Math.max(best.trueMembers.length, 1);
		const secondCenter = ((second.startSector + second.spanSectors / 2) % sectorCount) * sectorRad;
		let d = Math.abs(secondCenter - arcCenterAngle) % (Math.PI * 2);
		if(d > Math.PI) d = Math.PI * 2 - d;
		secondArcSeparationDeg = d * 180 / Math.PI;
	}

	return {
		rawCoverageDeg,
		supportedCoverageDeg: (best.trueMembers.length / sectorCount) * 360,
		supportedSectorMask,
		arcCenterAngle,
		arcSpanDeg: best.spanSectors * (360 / sectorCount),
		sectorCount,
		runCount: runs.length,
		secondArcRel,
		secondArcSeparationDeg,
	};
}

// Longitudinal angular-continuity filter (spec metode 8, check 1): the
// root-cause reason SUPPORTED coverage can still read ~2× the true visible arc
// on a real trench (226° vs ~120° ground truth) is that `computeSurfaceSupport`
// is purely per-cross-section - backfill that momentarily sits within the
// radial tolerance of R over a wide angular span passes, and the contiguous-run
// bridge then fuses it onto the genuine top arc. Real pipe surface is a
// CONNECTED strip in (station, sector); backfill contact is far patchier along
// the pipe. A supported sector at station k only survives if the same sector
// (± 1, for a gently curving pipe) is also supported at k-1 OR k+1. Endpoints
// lean on their single available neighbour. `null` masks (the synthetic init
// row / a pre-made init) are "open" and support every sector. Returns NEW mask
// arrays; inputs are untouched.
export function longitudinalContinuityFilter(masks){
	const n = masks.length;
	if(n < 3) return masks.map((m) => (m ? m.slice() : m));
	const supAt = (k, s) => {
		const m = masks[k];
		if(!m) return true;
		const c = m.length;
		return m[s] || m[(s - 1 + c) % c] || m[(s + 1) % c];
	};
	return masks.map((m, k) => {
		if(!m) return m;
		const prevOpen = k === 0;
		const nextOpen = k === n - 1;
		return m.map((v, s) => {
			if(!v) return false;
			if(prevOpen) return supAt(k + 1, s);
			if(nextOpen) return supAt(k - 1, s);
			return supAt(k - 1, s) || supAt(k + 1, s);
		});
	});
}

// Supported degrees implied by a boolean sector mask (inverse of the count →
// degrees the per-section computeSurfaceSupport does), for recomputing
// coverage after longitudinalContinuityFilter.
export function maskCoverageDeg(mask){
	if(!mask) return null;
	const t = mask.filter(Boolean).length;
	return (t / mask.length) * 360;
}

// ── station-sequence surface classification model (spec: classification
// beyond centreline+radial band) ───────────────────────────────────────────
//
// A classification is a sequence of stations along the accepted track, each
// carrying the LOCAL cross-section (centre, tangent, radius, band) plus
// which angular sectors were actually observed as pipe surface there
// (`supportedSectorMask`, or null meaning "no restriction" - used only for
// the single synthetic initial-model row, which is exactly where the user
// pointed). A query point is classified against the nearest bracketing pair
// of stations, interpolating linearly along the axis so support does not
// jump sector-hard from one station to the next.
// `appearance` (optional): {pointSimilarity(rgb)->[0,1]|null, strength:[0,1]}.
// When present and a query point carries RGB, the surface weight is multiplied
// by a soft appearance factor whose influence scales with `strength` (how
// discriminative colour is here - pipe vs background). NEVER a hard filter;
// with strength 0 or no RGB it has no effect (spec).
export function buildSurfaceClassification(stations, sigmaSurface, appearance = null){
	return {
		stations: stations.map((s) => ({
			center: s.center.clone ? s.center.clone() : {...s.center},
			tangent: s.tangent.clone ? s.tangent.clone() : {...s.tangent},
			radius: s.radius,
			band: s.band,
			supportedSectorMask: s.supportedSectorMask || null,
		})),
		sigmaSurface,
		appearance: appearance && typeof appearance.pointSimilarity === "function" && appearance.strength > 0 ? appearance : null,
	};
}

function toVec(p){
	return p && p.clone ? p : {x: p.x, y: p.y, z: p.z, clone(){ return toVec(this); }, sub(o){ return toVec({x: this.x - o.x, y: this.y - o.y, z: this.z - o.z}); }, dot(o){ return this.x * o.x + this.y * o.y + this.z * o.z; }, length(){ return Math.hypot(this.x, this.y, this.z); }};
}

// Finds the two bracketing stations (by nearest-segment projection, same
// convention as the classification's stations being consecutive positions
// along the track) and an interpolation parameter s in [0,1] between them.
// Returns null if the point doesn't project onto any station segment.
function locateOnStations(point, stations){
	let best = null;
	for(let i = 0; i < stations.length - 1; i++){
		const a = stations[i].center, b = stations[i + 1].center;
		const abx = b.x - a.x, aby = b.y - a.y, abz = b.z - a.z;
		const len2 = abx * abx + aby * aby + abz * abz;
		if(len2 < 1e-12) continue;
		const apx = point.x - a.x, apy = point.y - a.y, apz = point.z - a.z;
		const s = (apx * abx + apy * aby + apz * abz) / len2;
		if(s < 0 || s > 1) continue;
		const footx = a.x + abx * s, footy = a.y + aby * s, footz = a.z + abz * s;
		const d = Math.hypot(point.x - footx, point.y - footy, point.z - footz);
		if(!best || d < best.dist) best = {i, s, dist: d};
	}
	return best;
}

// perpendicular-plane angle of `point` around `center` given `tangent`
function angleInSection(point, center, tangent){
	const dx = point.x - center.x, dy = point.y - center.y, dz = point.z - center.z;
	const tlen = Math.hypot(tangent.x, tangent.y, tangent.z) || 1;
	const tx = tangent.x / tlen, ty = tangent.y / tlen, tz = tangent.z / tlen;
	const along = dx * tx + dy * ty + dz * tz;
	let rx = dx - along * tx, ry = dy - along * ty, rz = dz - along * tz;
	const rlen = Math.hypot(rx, ry, rz);
	if(rlen < 1e-9) return {radial: 0, angle: 0, u: {x: 1, y: 0, z: 0}, v: {x: 0, y: 1, z: 0}};
	// deterministic orthonormal basis matching cylinderFit.js's orthonormalBasis()
	const helper = Math.abs(tz) < 0.9 ? {x: 0, y: 0, z: 1} : {x: 1, y: 0, z: 0};
	const ux = helper.y * tz - helper.z * ty, uy = helper.z * tx - helper.x * tz, uz = helper.x * ty - helper.y * tx;
	const ulen = Math.hypot(ux, uy, uz) || 1;
	const u = {x: ux / ulen, y: uy / ulen, z: uz / ulen};
	const vx = ty * u.z - tz * u.y, vy = tz * u.x - tx * u.z, vz = tx * u.y - ty * u.x;
	const v = {x: vx, y: vy, z: vz};
	const px = rx * u.x + ry * u.y + rz * u.z;
	const py = rx * v.x + ry * v.y + rz * v.z;
	let angle = Math.atan2(py, px);
	if(angle < 0) angle += Math.PI * 2;
	return {radial: rlen, angle};
}

// Soft support weight in [0,1] at an interpolated station: sector occupancy
// blended linearly between the two bracketing stations' own masks (spec:
// "Surface support mellem nabosektioner må gerne interpoleres... undgå
// kantede classification-skift"). `null` mask = fully open (no restriction).
function interpolatedSupportWeight(angle, maskA, maskB, s, sectorCount){
	const sectorRad = (Math.PI * 2) / sectorCount;
	const sector = Math.min(sectorCount - 1, Math.floor(angle / sectorRad));
	const wA = maskA ? (maskA[sector] ? 1 : 0) : 1;
	const wB = maskB ? (maskB[sector] ? 1 : 0) : 1;
	return wA * (1 - s) + wB * s;
}

// Continuous evidence in [0,1] that `point` belongs to the observed pipe
// SURFACE (not just "somewhere in the radial band"): radial closeness AND
// angular surface support, both required. `robustWeight` matches
// cylinderFit.js's Cauchy-style weighting convention.
function robustWeight(residual, scale){
	const r = residual / Math.max(scale, 1e-6);
	return 1 / (1 + r * r);
}

export function surfaceInlierWeight(point, classification){
	const p = toVec(point);
	const loc = locateOnStations(p, classification.stations);
	if(!loc) return 0;
	const a = classification.stations[loc.i], b = classification.stations[loc.i + 1];
	const center = {x: a.center.x + (b.center.x - a.center.x) * loc.s, y: a.center.y + (b.center.y - a.center.y) * loc.s, z: a.center.z + (b.center.z - a.center.z) * loc.s};
	const tangent = {x: a.tangent.x + (b.tangent.x - a.tangent.x) * loc.s, y: a.tangent.y + (b.tangent.y - a.tangent.y) * loc.s, z: a.tangent.z + (b.tangent.z - a.tangent.z) * loc.s};
	const radius = a.radius + (b.radius - a.radius) * loc.s;
	const band = a.band + (b.band - a.band) * loc.s;
	const {radial, angle} = angleInSection(p, center, tangent);
	const radialResidual = Math.abs(radial - radius);
	if(radialResidual > band * 1.5) return 0; // hard cutoff far outside any plausible band
	const radialWeight = robustWeight(radialResidual, Math.max(classification.sigmaSurface, band / 3));
	const sectorCount = (a.supportedSectorMask || b.supportedSectorMask || []).length || 36;
	const surfaceWeight = interpolatedSupportWeight(angle, a.supportedSectorMask, b.supportedSectorMask, loc.s, sectorCount);
	let appWeight = 1;
	const app = classification.appearance;
	const rgb = point && point.rgb ? point.rgb : null;
	if(app && rgb){
		const sim = app.pointSimilarity(rgb);
		if(sim != null) appWeight = 1 - Math.min(0.85, app.strength) * (1 - sim);
	}
	return radialWeight * surfaceWeight * appWeight;
}

export function isSurfaceInlier(point, classification, threshold = 0.5){
	return surfaceInlierWeight(point, classification) >= threshold;
}
